import Fuse from 'fuse.js';
import Hotkeys from 'react-hot-keys';
import { ChangeEvent, Fragment, PureComponent, ReactNode } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { GTreatmentPackage, GTreatmentService } from '../../store/db';
import { getDoc, getGArrayWithClinicId, upsertTreatmentPackage } from '../../store/idbHandler';
import { captureError } from '../../pages/captureError';
import { notification } from 'antd';
import { getMyDbId } from '../../api/mactiveid';
import { DB_TABLES } from '../../store/dbTables';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { GO_TO_SETTINGS } from '../../routes/paths';
import { v4 } from 'uuid';
import createTreatmentPackage from '../../api/createTreatmentPackage';
import { SlideFromRightWrapper } from '../common/SlideFromRightWrapper';
import { formattedNumber } from '../../pages/utils';

const fuseConfig = {
    keys: ['_name'],
    threshold: 0.2,
    ignoreLocation: true,
    useExtendedSearch: true,
};

interface Props extends RouteComponentProps {
    closeMe: (refresh?: boolean) => void;
    editTreatmentPackage?: GTreatmentPackage;
    clinic_id?: string;
}

interface State {
    isFetchingTreatmentServices: boolean;
    treatmentServices: GTreatmentService[];
    searchFilter?: string;
    selectedTreatmentServices: GTreatmentService[];
    newTreatmentPackage?: GTreatmentPackage;
    showNewTreatmentPackage?: boolean;
    isSavingTreatmentServices?: boolean;
}

class CreateOrEditTreatmentPackage extends PureComponent<Props, State> {
    treatmentServicesFuse = new Fuse([] as GTreatmentService[], fuseConfig);
    doc = getDoc();

    state: State = {
        isFetchingTreatmentServices: true,
        treatmentServices: [],
        selectedTreatmentServices: [],
    };

    componentDidMount = async () => {
        const { editTreatmentPackage, clinic_id } = this.props;
        const { setupTreatmentServicesFuse } = this;
        try {
            let gTreatmentServices = await getGArrayWithClinicId<GTreatmentService>(
                getMyDbId() || '',
                DB_TABLES['treatmentServices'],
                clinic_id || '',
            );
            gTreatmentServices = gTreatmentServices.filter((i) => !i.archive);
            this.setState({
                isFetchingTreatmentServices: false,
                treatmentServices: gTreatmentServices.sort((a, b) => {
                    const dateA = a.updated_at ? new Date(a.updated_at) : null;
                    const dateB = b.updated_at ? new Date(b.updated_at) : null;
                    return (dateB as any) - (dateA as any);
                }),
                newTreatmentPackage: editTreatmentPackage,
                selectedTreatmentServices: editTreatmentPackage?.services || [],
            });
            setupTreatmentServicesFuse(gTreatmentServices);
        } catch (err) {
            captureError(err as Error);
            notification.error({
                message: 'Error while fetching treatment services',
                duration: 5,
            });
            this.setState({
                isFetchingTreatmentServices: false,
            });
        }
    };

    setupTreatmentServicesFuse = (treatmentPackages: GTreatmentService[]): void => {
        this.treatmentServicesFuse = new Fuse(
            treatmentPackages.map((t) => ({
                ...t,
                _name: t.name,
            })),
            fuseConfig,
        );
    };

    onSearchInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            searchFilter: e.target.value,
        });
    };

    clearSearchFilter = (): void => {
        this.setState({
            searchFilter: '',
        });
    };

    onChange = (e: CheckboxChangeEvent, service: GTreatmentService): void => {
        let { selectedTreatmentServices } = this.state;
        const isChecked = e.target.checked;
        if (isChecked) {
            selectedTreatmentServices.push(service);
        } else {
            selectedTreatmentServices = selectedTreatmentServices.filter(
                (i) => i._id !== service._id,
            );
        }
        this.setState({ selectedTreatmentServices: Array.from(selectedTreatmentServices) });
    };

    onAddNewServices = () => {
        const { history } = this.props;
        history.push(GO_TO_SETTINGS('manage-services'));
    };

    onNext = () => {
        const { editTreatmentPackage, clinic_id } = this.props;
        const { selectedTreatmentServices } = this.state;
        const { doc } = this;
        const price = selectedTreatmentServices.reduce((sum, i) => sum + i.price, 0);
        const businessId = doc?.profile?.professional?.business?.id;
        const newTreatmentPackage = {
            _id: editTreatmentPackage?._id || '',
            package_id: editTreatmentPackage?._id || v4(),
            services: selectedTreatmentServices,
            price: price,
            created_at: editTreatmentPackage?.created_at
                ? new Date(editTreatmentPackage?.created_at).toISOString()
                : new Date().toISOString(),
            updated_at: new Date().toISOString(),
            owner_id: editTreatmentPackage?.owner_id || doc?._id || businessId || '',
            name: editTreatmentPackage?.name || '',
            archive: false,
            clinic_id: clinic_id || '',
        };
        this.setState({ newTreatmentPackage: newTreatmentPackage, showNewTreatmentPackage: true });
    };

    onBack = () => {
        this.setState({ showNewTreatmentPackage: false });
    };

    onDeleteService = (_id: string) => {
        let { selectedTreatmentServices } = this.state;
        selectedTreatmentServices = selectedTreatmentServices.filter((i) => i._id !== _id);
        this.setState({ selectedTreatmentServices: Array.from(selectedTreatmentServices) });
    };

    onSave = async () => {
        const { closeMe } = this.props;
        const { newTreatmentPackage, selectedTreatmentServices } = this.state;
        if (!newTreatmentPackage?.name.trim()) {
            notification.error({
                message: 'Please enter a treatment package name',
                duration: 5,
            });
            return;
        }
        this.setState({ isSavingTreatmentServices: true });
        try {
            if (newTreatmentPackage) {
                const price = selectedTreatmentServices.reduce((sum, i) => sum + i.price, 0);
                newTreatmentPackage.price = price;
                newTreatmentPackage.services = selectedTreatmentServices;
                const { success, data, error } = await createTreatmentPackage(newTreatmentPackage);
                if (success) {
                    newTreatmentPackage._id = data._id;
                    await upsertTreatmentPackage(newTreatmentPackage);
                } else {
                    newTreatmentPackage._id = `locale-${v4()}`;
                    await upsertTreatmentPackage(newTreatmentPackage);
                }
                if (error) {
                    notification.error({
                        message: 'Error while saving treatment service',
                        duration: 5,
                    });
                }
            }
        } catch (err) {
            captureError(err as Error);
            notification.error({
                message: 'Error while saving treatment service',
                duration: 5,
            });
        } finally {
            this.setState({ isSavingTreatmentServices: false });
            closeMe(true);
        }
    };

    render(): ReactNode {
        const { closeMe, editTreatmentPackage } = this.props;
        const {
            treatmentServices,
            searchFilter,
            selectedTreatmentServices,
            showNewTreatmentPackage,
            newTreatmentPackage,
            isSavingTreatmentServices,
        } = this.state;
        const {
            treatmentServicesFuse,
            onSearchInputChange,
            clearSearchFilter,
            onChange,
            onAddNewServices,
            onNext,
            onBack,
            onDeleteService,
            onSave,
        } = this;
        const selectedTreatmentServicesSet = new Set(selectedTreatmentServices.map((i) => i._id));
        const treatmentServicesSet = new Set(treatmentServices.map((i) => i._id));
        let filteredTreatmentServices: GTreatmentService[] = treatmentServices;
        filteredTreatmentServices = treatmentServices.filter(
            (i) => !selectedTreatmentServicesSet.has(i._id),
        );
        if (searchFilter) {
            filteredTreatmentServices = treatmentServicesFuse
                .search({
                    $or: [{ _name: searchFilter }],
                })
                .map((i) => i.item);
        }

        const totalPrice = selectedTreatmentServices.reduce((acc, cur) => acc + cur.price, 0);

        return (
            <Hotkeys keyName="esc">
                <SlideFromRightWrapper closeMe={() => closeMe()} style={{ width: '940px' }}>
                    <div className="h-full flex flex-col overflow-hidden">
                        <div className="bg-white px-16 py-12 flex items-center justify-between border-b border-darwin-neutral_new-200">
                            <p className="text-20">
                                {editTreatmentPackage ? 'Edit' : 'New'} Treatment Package
                            </p>
                            <svg
                                className="w-14 h-14 fill-darwin-neutral_new-800 cursor-pointer"
                                viewBox="0 0 12 12"
                                xmlns="http://www.w3.org/2000/svg"
                                onClick={() => closeMe()}
                            >
                                <path d="M10.7812 2.28125L7.03125 6.03125L10.75 9.75C11.0625 10.0312 11.0625 10.5 10.75 10.7812C10.4688 11.0938 10 11.0938 9.71875 10.7812L5.96875 7.0625L2.25 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 2 0.90625 1.53125 1.21875 1.21875C1.5 0.9375 1.96875 0.9375 2.28125 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.7812 1.25C11.0625 1.53125 11.0625 2 10.7812 2.28125Z" />
                            </svg>
                        </div>
                        <div className="flex-1 flex flex-col overflow-hidden bg-darwin-neutrals-50">
                            {showNewTreatmentPackage ? (
                                <Fragment>
                                    <div className="flex-1 flex flex-col overflow-hidden space-y-16 p-16">
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 gap-x-24">
                                                <div className="space-y-4">
                                                    <p className="text-13 font-700">Package Name</p>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter package name..."
                                                        className="w-full px-12 py-10 border border-darwin-neutral_new-200 rounded-8"
                                                        value={newTreatmentPackage?.name || ''}
                                                        onChange={(e) =>
                                                            this.setState({
                                                                newTreatmentPackage: {
                                                                    ...newTreatmentPackage,
                                                                    name: e.target.value,
                                                                } as GTreatmentPackage,
                                                            })
                                                        }
                                                    />
                                                </div>
                                                <div className="space-y-4">
                                                    <p className="text-13 font-700">Total Amount</p>
                                                    <div className="flex items-center w-full border border-darwin-neutral_new-200 rounded-8 overflow-hidden">
                                                        <p className="px-12 py-10 bg-white">₹</p>
                                                        <p
                                                            className="flex-1 pl-8 py-10"
                                                            style={{ background: '#E8E8E8' }}
                                                        >
                                                            ₹{formattedNumber(totalPrice)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex overflow-auto">
                                            <table
                                                style={{ borderWidth: 0.5 }}
                                                className="w-full border-darwin-neutral_new-200 rounded-t-8"
                                            >
                                                <tr
                                                    style={{ background: '#E8E8E8' }}
                                                    className="font-700 uppercase text-11 rounded-t-8"
                                                >
                                                    <td
                                                        className="px-12 py-8 border-darwin-neutral_new-200"
                                                        style={{
                                                            width: '10%',
                                                            borderWidth: 0.5,
                                                        }}
                                                    >
                                                        S. No.
                                                    </td>
                                                    <td
                                                        className="px-12 py-8 border-darwin-neutral_new-200"
                                                        style={{
                                                            width: '65%',
                                                            borderWidth: 0.5,
                                                        }}
                                                    >
                                                        Service
                                                    </td>
                                                    <td
                                                        className="px-12 py-8 border-darwin-neutral_new-200"
                                                        style={{
                                                            width: '15%',
                                                            borderWidth: 0.5,
                                                        }}
                                                    >
                                                        Amount
                                                    </td>
                                                    <td
                                                        className="px-12 py-8 border-darwin-neutral_new-200 text-right"
                                                        style={{
                                                            width: '10%',
                                                            borderWidth: 0.5,
                                                        }}
                                                    >
                                                        Action
                                                    </td>
                                                </tr>
                                                {selectedTreatmentServices.map((i, index) => (
                                                    <tr className="bg-white">
                                                        <td
                                                            className="px-12 py-8 border-darwin-neutral_new-200"
                                                            style={{ borderWidth: 0.5 }}
                                                        >
                                                            {index + 1}
                                                        </td>
                                                        <td
                                                            className="px-12 py-8 border-darwin-neutral_new-200"
                                                            style={{ borderWidth: 0.5 }}
                                                        >
                                                            {i.name}
                                                        </td>
                                                        <td
                                                            className="px-12 py-8 border-darwin-neutral_new-200"
                                                            style={{ borderWidth: 0.5 }}
                                                        >
                                                            ₹{formattedNumber(i.price)}
                                                        </td>
                                                        <td
                                                            className="px-12 py-8 border-darwin-neutral_new-200"
                                                            style={{ borderWidth: 0.5 }}
                                                        >
                                                            <div className="flex justify-end">
                                                                <button
                                                                    onClick={() =>
                                                                        onDeleteService(i._id)
                                                                    }
                                                                    disabled={
                                                                        selectedTreatmentServices.length ===
                                                                        1
                                                                    }
                                                                    className="disabled:opacity-70"
                                                                >
                                                                    <svg
                                                                        className="w-12 h-14 fill-darwin-neutrals-600"
                                                                        viewBox="0 0 12 14"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path d="M4.80664 1.34375C4.73047 1.34375 4.67969 1.39453 4.62891 1.44531L4.14648 2.15625H7.82812L7.3457 1.44531C7.32031 1.39453 7.24414 1.34375 7.16797 1.34375H4.80664ZM9.30078 2.15625H9.63086H10.875H11.0781C11.4082 2.15625 11.6875 2.43555 11.6875 2.76562C11.6875 3.12109 11.4082 3.375 11.0781 3.375H10.7734L10.1641 11.627C10.0879 12.4902 9.40234 13.125 8.53906 13.125H3.43555C2.57227 13.125 1.88672 12.4902 1.81055 11.627L1.20117 3.375H0.921875C0.566406 3.375 0.3125 3.12109 0.3125 2.76562C0.3125 2.43555 0.566406 2.15625 0.921875 2.15625H1.125H2.34375H2.67383L3.61328 0.759766C3.86719 0.378906 4.32422 0.125 4.80664 0.125H7.16797C7.65039 0.125 8.10742 0.378906 8.36133 0.759766L9.30078 2.15625ZM9.55469 3.375H2.41992L3.0293 11.5508C3.05469 11.7539 3.23242 11.9062 3.43555 11.9062H8.53906C8.74219 11.9062 8.91992 11.7539 8.94531 11.5508L9.55469 3.375Z" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </table>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between px-16 py-12 bg-white border-t border-darwin-neutral_new-200">
                                        <button
                                            className="px-9 py-5 rounded-4 border border-darwin-accent-bright-blue-800"
                                            onClick={onBack}
                                        >
                                            <p className="text-13 font-700 text-darwin-accent-bright-blue-800">
                                                Back
                                            </p>
                                        </button>
                                        <button
                                            className="px-10 py-6 flex items-center space-x-6 rounded-4 bg-darwin-accent-bright-blue-800 disabled:opacity-70"
                                            onClick={onSave}
                                            disabled={selectedTreatmentServicesSet.size === 0}
                                        >
                                            <p className="text-13 font-700 text-white">Save</p>
                                            {isSavingTreatmentServices ? (
                                                <svg
                                                    className="w-12 h-10 fill-white animate-spin"
                                                    role="img"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                >
                                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                                </svg>
                                            ) : (
                                                <svg
                                                    className="w-12 h-10 fill-white"
                                                    viewBox="0 0 12 11"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M11.4844 5.58203L7.01562 9.84766C6.76172 10.0762 6.38086 10.0762 6.15234 9.82227C5.92383 9.56836 5.92383 9.1875 6.17773 8.95898L9.55469 5.73438H0.921875C0.566406 5.73438 0.3125 5.48047 0.3125 5.125C0.3125 4.79492 0.566406 4.51562 0.921875 4.51562H9.55469L6.17773 1.31641C5.92383 1.08789 5.92383 0.681641 6.15234 0.453125C6.38086 0.199219 6.78711 0.199219 7.01562 0.427734L11.4844 4.69336C11.6113 4.82031 11.6875 4.97266 11.6875 5.125C11.6875 5.30273 11.6113 5.45508 11.4844 5.58203Z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </Fragment>
                            ) : (
                                <Fragment>
                                    <div className="flex-1 flex flex-col overflow-auto space-y-16 p-16">
                                        <div className="space-y-12">
                                            <div className="flex items-center justify-between">
                                                <p className="text-16 font-700">
                                                    Select services{' '}
                                                    {selectedTreatmentServicesSet.size > 0 &&
                                                        `(${selectedTreatmentServicesSet.size} selected)`}
                                                </p>
                                                <button
                                                    className="px-8 py-4 flex items-center space-x-6 rounded-4 bg-darwin-accent-bright-blue-800"
                                                    onClick={onAddNewServices}
                                                >
                                                    <svg
                                                        className="w-12 h-10 fill-white"
                                                        viewBox="0 0 12 12"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M6.8125 1.65625V5.3125H10.4688C10.9004 5.3125 11.2812 5.69336 11.2812 6.125C11.2812 6.58203 10.9004 6.9375 10.4688 6.9375H6.8125V10.5938C6.8125 11.0508 6.43164 11.4062 6 11.4062C5.54297 11.4062 5.1875 11.0508 5.1875 10.5938V6.9375H1.53125C1.07422 6.9375 0.71875 6.58203 0.71875 6.125C0.71875 5.69336 1.07422 5.3125 1.53125 5.3125H5.1875V1.65625C5.1875 1.22461 5.54297 0.84375 6 0.84375C6.43164 0.84375 6.8125 1.22461 6.8125 1.65625Z" />
                                                    </svg>
                                                    <p className="text-13 font-700 text-white">
                                                        Add new
                                                    </p>
                                                </button>
                                            </div>
                                            <div className="border border-darwin-neutral_new-200 px-14 py-8 rounded-8 flex items-center space-x-12 bg-white">
                                                {searchFilter ? (
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 512 512"
                                                        className="w-15 h-15"
                                                        onClick={clearSearchFilter}
                                                    >
                                                        <path
                                                            d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm121.6 313.1c4.7 4.7 4.7 12.3 0 17L338 377.6c-4.7 4.7-12.3 4.7-17 0L256 312l-65.1 65.6c-4.7 4.7-12.3 4.7-17 0L134.4 338c-4.7-4.7-4.7-12.3 0-17l65.6-65-65.6-65.1c-4.7-4.7-4.7-12.3 0-17l39.6-39.6c4.7-4.7 12.3-4.7 17 0l65 65.7 65.1-65.6c4.7-4.7 12.3-4.7 17 0l39.6 39.6c4.7 4.7 4.7 12.3 0 17L312 256l65.6 65.1z"
                                                            fill="#616A8F"
                                                        />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className="w-15 h-15"
                                                        viewBox="0 0 15 15"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M10.297 6.4375C10.297 4.87891 9.44934 3.45703 8.1095 2.66406C6.74231 1.87109 5.07434 1.87109 3.7345 2.66406C2.36731 3.45703 1.547 4.87891 1.547 6.4375C1.547 8.02344 2.36731 9.44531 3.7345 10.2383C5.07434 11.0312 6.74231 11.0312 8.1095 10.2383C9.44934 9.44531 10.297 8.02344 10.297 6.4375ZM9.44934 10.9219C8.46497 11.6875 7.2345 12.125 5.922 12.125C2.77747 12.125 0.234497 9.58203 0.234497 6.4375C0.234497 3.32031 2.77747 0.75 5.922 0.75C9.03918 0.75 11.6095 3.32031 11.6095 6.4375C11.6095 7.77734 11.1447 9.00781 10.379 9.99219L14.0431 13.6289C14.2892 13.9023 14.2892 14.3125 14.0431 14.5586C13.7697 14.832 13.3595 14.832 13.1134 14.5586L9.44934 10.9219Z"
                                                            fill="#616A8F"
                                                        />
                                                    </svg>
                                                )}
                                                <input
                                                    type="text"
                                                    placeholder="Search for services..."
                                                    onChange={onSearchInputChange}
                                                    value={searchFilter}
                                                    className="flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-1 overflow-auto space-y-10">
                                            {!searchFilter && selectedTreatmentServices.length > 0 && (
                                                <div>
                                                    {selectedTreatmentServices.map((i, index) => (
                                                        <div
                                                            className={`bg-white ${
                                                                index + 1 !==
                                                                selectedTreatmentServices.length
                                                                    ? 'border-b'
                                                                    : ''
                                                            } border-darwin-neutral_new-200 px-16 py-12 ${
                                                                index === 0 ? 'rounded-t-8' : ''
                                                            } ${
                                                                index + 1 ===
                                                                selectedTreatmentServices.length
                                                                    ? 'rounded-b-8'
                                                                    : ''
                                                            } flex items-center justify-between text-13`}
                                                        >
                                                            <Checkbox
                                                                onChange={(
                                                                    e: CheckboxChangeEvent,
                                                                ) => onChange(e, i)}
                                                                className="font-700 text-13"
                                                                checked={selectedTreatmentServicesSet.has(
                                                                    i._id,
                                                                )}
                                                            >
                                                                <span>{i.name}</span>{' '}
                                                                {!treatmentServicesSet.has(
                                                                    i._id,
                                                                ) && (
                                                                    <span className="text-red-primary">
                                                                        (Archived)
                                                                    </span>
                                                                )}
                                                            </Checkbox>
                                                            <p>₹{formattedNumber(i.price)}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {filteredTreatmentServices.length > 0 ? (
                                                <div>
                                                    {filteredTreatmentServices.map((i, index) => (
                                                        <div
                                                            className={`bg-white ${
                                                                index + 1 !==
                                                                filteredTreatmentServices.length
                                                                    ? 'border-b'
                                                                    : ''
                                                            } border-darwin-neutral_new-200 px-16 py-12 ${
                                                                index === 0 ? 'rounded-t-8' : ''
                                                            } ${
                                                                index + 1 ===
                                                                filteredTreatmentServices.length
                                                                    ? 'rounded-b-8'
                                                                    : ''
                                                            } flex items-center justify-between text-13`}
                                                        >
                                                            <Checkbox
                                                                onChange={(
                                                                    e: CheckboxChangeEvent,
                                                                ) => onChange(e, i)}
                                                                className="font-700 text-13"
                                                                checked={selectedTreatmentServicesSet.has(
                                                                    i._id,
                                                                )}
                                                            >
                                                                {i.name}
                                                            </Checkbox>
                                                            <p>₹{formattedNumber(i.price)}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="pl-4">No services found!</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between px-16 py-12 bg-white border-t border-darwin-neutral_new-200">
                                        <button
                                            className="px-9 py-5 rounded-4 border border-darwin-accent-bright-blue-800"
                                            onClick={() => closeMe()}
                                        >
                                            <p className="text-13 font-700 text-darwin-accent-bright-blue-800">
                                                Cancel
                                            </p>
                                        </button>
                                        <button
                                            className="px-10 py-6 flex items-center space-x-6 rounded-4 bg-darwin-accent-bright-blue-800 disabled:opacity-70"
                                            onClick={onNext}
                                            disabled={selectedTreatmentServicesSet.size === 0}
                                        >
                                            <p className="text-13 font-700 text-white">Next</p>
                                            <svg
                                                className="w-12 h-10 fill-white"
                                                viewBox="0 0 12 11"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M11.4844 5.58203L7.01562 9.84766C6.76172 10.0762 6.38086 10.0762 6.15234 9.82227C5.92383 9.56836 5.92383 9.1875 6.17773 8.95898L9.55469 5.73438H0.921875C0.566406 5.73438 0.3125 5.48047 0.3125 5.125C0.3125 4.79492 0.566406 4.51562 0.921875 4.51562H9.55469L6.17773 1.31641C5.92383 1.08789 5.92383 0.681641 6.15234 0.453125C6.38086 0.199219 6.78711 0.199219 7.01562 0.427734L11.4844 4.69336C11.6113 4.82031 11.6875 4.97266 11.6875 5.125C11.6875 5.30273 11.6113 5.45508 11.4844 5.58203Z" />
                                            </svg>
                                        </button>
                                    </div>
                                </Fragment>
                            )}
                        </div>
                    </div>
                </SlideFromRightWrapper>
            </Hotkeys>
        );
    }
}

export default withRouter(CreateOrEditTreatmentPackage);
